import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import SearchSelect from './SearchSelect';
import { sickCallSummary } from '../utils/sickCall';

// The phone's "Log time off for someone" sheet — an Executive or the office putting in
// a vacation someone texted them about, or a sick call that came in by phone (Pat,
// Sep 2026). Same route as the desktop Time Off page: it goes in already approved, the
// person gets a bell, their PM / field supervisor / the office get the FYI, and the
// Sunday notes go on the QuickBooks schedule.
const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}
const KINDS = [['vacation', '🌴 Vacation'], ['sick', '🤒 Sick'], ['personal', '🏠 Personal'], ['wfh', '🏡 Work from home'], ['other', '📝 Other']];
const todayYmd = () => new Date().toLocaleDateString('en-CA');
const lab = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 };
const inp = { width: '100%', boxSizing: 'border-box', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 12px', fontSize: 15, color: '#111', background: '#fff', fontFamily: 'inherit' };

export default function LogTimeOffSheet({ onClose }) {
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ user_id: '', kind: 'vacation', start_date: todayYmd(), end_date: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);   // the server's answer once logged
  const [err, setErr] = useState('');
  useEffect(() => {
    apiFetch('/users').then(u => setPeople((Array.isArray(u) ? u : []).filter(x => (x.status || 'active') === 'active' && !x.isAgent && !x.is_agent))).catch(() => {});
  }, []);
  const name = u => `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim();
  const who = people.find(p => String(p.id) === String(form.user_id));

  async function submit() {
    if (!form.user_id) return setErr('Pick who it is for.');
    if (!form.start_date) return setErr('Pick the first day.');
    if (form.end_date && form.end_date < form.start_date) return setErr('The last day is before the first.');
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/time-off/log', { method: 'POST', body: JSON.stringify({ user_id: form.user_id, kind: form.kind, start_date: form.start_date, end_date: form.end_date || form.start_date, reason: form.reason }) });
      setDone(r);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>Log time off for someone</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 20px calc(20px + env(safe-area-inset-bottom, 0px))' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>{done.kind === 'sick' ? `${who ? name(who) : 'Sick day'} logged sick` : done.kind === 'wfh' ? `${who ? name(who) : 'Someone'} is working from home — noted` : `${who ? name(who) : 'Time off'} is on the books`}</div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, lineHeight: 1.45 }}>
                {done.kind === 'sick'
                  ? sickCallSummary(done.schedule)
                  : done.scheduled && done.scheduled.ok
                    ? 'They got the bell, their PM and the office have the FYI, and it is on the QuickBooks schedule.'
                    : 'They got the bell and their PM and the office have the FYI. QuickBooks did not take the Sunday note — use "Put on the schedule" on the Time Off page.'}
              </div>
              <button onClick={() => { setDone(null); setForm({ user_id: '', kind: 'vacation', start_date: todayYmd(), end_date: '', reason: '' }); }}
                style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Log another
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={lab}>Who</label>
                <SearchSelect value={form.user_id} onChange={id => setForm(f => ({ ...f, user_id: id }))} placeholder="Type a name…" inputStyle={inp}
                  options={people.slice().sort((a, b) => name(a).localeCompare(name(b))).map(u => ({ value: u.id, label: name(u), sub: u.role === 'Accounting' ? 'Office' : u.role }))} />
              </div>
              <div>
                <label style={lab}>What kind</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {KINDS.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setForm(f => ({ ...f, kind: k }))}
                      style={{ padding: '8px 13px', borderRadius: 999, border: `1.5px solid ${form.kind === k ? '#e87722' : '#e5e7eb'}`, background: form.kind === k ? '#fff7ed' : '#fff', color: form.kind === k ? '#9a3412' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  {form.kind === 'sick' ? 'Recorded straight away. Their PM, the field supervisor and the office are told.' : form.kind === 'wfh' ? 'Noted straight away — they are working, just not at the office. It goes on the calendar and the Executives get an FYI; nothing moves on the schedule.' : 'Goes in as approved. They get a bell, their PM and the office get the FYI, and it goes on the QuickBooks schedule as Sunday notes.'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
                <div>
                  <label style={lab}>First day</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={lab}>Last day <span style={{ fontWeight: 500, textTransform: 'none' }}>(if more)</span></label>
                  <input type="date" value={form.end_date} min={form.start_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inp} />
                </div>
              </div>
              <div>
                <label style={lab}>Note (optional)</label>
                <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder={form.kind === 'sick' ? 'Flu, doctor, kid\'s sick…' : 'Texted me Tuesday, camping trip…'} style={inp} />
              </div>
              {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <button onClick={submit} disabled={busy}
                style={{ width: '100%', padding: '14px 0', background: busy ? '#e5e7eb' : '#e87722', color: busy ? '#9ca3af' : '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                {busy ? 'Logging…' : form.kind === 'sick' ? 'Log the sick day' : form.kind === 'wfh' ? 'Note it' : 'Log it'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
